import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Breadcrumb from "@/components/base/Breadcrumb";
import usePageMeta from "@/hooks/usePageMeta";
import { categories, topComments } from "@/mocks/events";
import { formatCount } from "@/utils/format";
import RankItem from "./components/RankItem";

function catName(key: string): string {
  const found = categories.find((c) => c.key === key);
  return found ? found.name : "";
}

export default function Comments() {
  const [activeCat, setActiveCat] = useState("all");
  const [sort, setSort] = useState<"hot" | "new">("hot");

  usePageMeta({
    title: "神评榜 · 热门吃瓜高赞神回复与网友热评排行",
    description:
      "瓜田热评神评榜汇总全站高赞评论与神回复排行，按点赞量和上榜时间排序，覆盖娱乐圈、社会热点、日常生活等分类，一页看完全网最有意思的网友观点。",
    keywords: "热门吃瓜高赞评论,热门吃瓜神回复,网友热评排行,吃瓜神评榜,热门吃瓜评论区",
    path: "/comments",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "神评榜 · 高赞神回复排行",
      description: "全站高赞评论与神回复排行，按点赞量与上榜时间排列。",
      inLanguage: "zh-CN",
      breadcrumb: {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "/" },
          { "@type": "ListItem", position: 2, name: "神评榜", item: "/comments" },
        ],
      },
      mainEntity: {
        "@type": "ItemList",
        name: "高赞神回复排行",
        itemListElement: topComments.map((c, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: `${c.nickname}：${c.content}`,
          url: `/event/${c.eventId}`,
        })),
      },
    },
  });

  const list = useMemo(() => {
    const filtered =
      activeCat === "all" ? [...topComments] : topComments.filter((c) => c.categoryName === catName(activeCat));
    if (sort === "hot") filtered.sort((a, b) => b.likes - a.likes);
    else filtered.sort((a, b) => (a.date < b.date ? 1 : -1));
    return filtered;
  }, [activeCat, sort]);

  const totalLikes = topComments.reduce((sum, c) => sum + c.likes, 0);

  return (
    <Layout>
      <section className="w-full border-b border-background-200 bg-background-100 px-4 py-10 md:px-8 md:py-14">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "神评榜" }]} />
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1 text-xs font-semibold text-accent-900">
              <i className="ri-trophy-line text-sm"></i>
              截至 {new Date().getFullYear()} 年高赞精选
            </span>
            <h1 className="mt-4 font-heading text-3xl font-black tracking-tight text-foreground-950 md:text-4xl lg:text-5xl">
              神评榜 · 一句话封神的围观群众
            </h1>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
              这里收录了整个瓜田里点赞最高的网友热评和神回复。它们有的犀利、有的温柔、有的让人拍桌，
              但共同点是——一句话就把现场说透了。
            </p>
          </div>

          <div className="grid grid-cols-3 gap-4 lg:col-span-5">
            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <p className="font-heading text-xl font-black text-primary-600 md:text-2xl">
                {topComments.length}
              </p>
              <p className="mt-1 text-xs text-foreground-500">上榜神评</p>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <p className="font-heading text-xl font-black text-accent-600 md:text-2xl">
                {formatCount(totalLikes)}
              </p>
              <p className="mt-1 text-xs text-foreground-500">累计赞同</p>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <p className="font-heading text-xl font-black text-secondary-600 md:text-2xl">5</p>
              <p className="mt-1 text-xs text-foreground-500">覆盖分类</p>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full px-4 py-8 md:px-8 md:py-12">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
          <div className="lg:col-span-8">
            <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setActiveCat("all")}
                  aria-pressed={activeCat === "all"}
                  className={`whitespace-nowrap rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors ${
                    activeCat === "all"
                      ? "border-primary-500 bg-primary-500 text-background-50"
                      : "border-background-200 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
                  }`}
                >
                  全部分类
                </button>
                {categories
                  .filter((c) => c.key !== "all")
                  .map((c) => (
                    <button
                      key={c.key}
                      type="button"
                      onClick={() => setActiveCat(c.key)}
                      aria-pressed={activeCat === c.key}
                      className={`whitespace-nowrap rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors ${
                        activeCat === c.key
                          ? "border-primary-500 bg-primary-500 text-background-50"
                          : "border-background-200 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
                      }`}
                    >
                      {c.name}
                    </button>
                  ))}
              </div>

              <div className="inline-flex self-start rounded-full bg-background-100 p-1">
                <button
                  type="button"
                  onClick={() => setSort("hot")}
                  aria-pressed={sort === "hot"}
                  className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${
                    sort === "hot" ? "bg-foreground-950 text-background-50" : "text-foreground-600"
                  }`}
                >
                  最高赞
                </button>
                <button
                  type="button"
                  onClick={() => setSort("new")}
                  aria-pressed={sort === "new"}
                  className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${
                    sort === "new" ? "bg-foreground-950 text-background-50" : "text-foreground-600"
                  }`}
                >
                  最新上榜
                </button>
              </div>
            </div>

            {list.length > 0 ? (
              <div className="flex flex-col gap-4">
                {list.map((item, index) => (
                  <RankItem key={item.id} item={item} rank={index + 1} />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                  <i className="ri-inbox-line text-3xl"></i>
                </span>
                <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">
                  该分类暂无上榜神评
                </h2>
                <p className="mt-2 text-sm text-foreground-600">换个分类看看，或者直接去瓜田广场逛现场。</p>
              </div>
            )}
          </div>

          <aside className="flex flex-col gap-5 lg:col-span-4">
            <div className="rounded-lg border border-background-200 bg-accent-50 p-5">
              <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-award-line text-lg text-accent-700"></i>
                上榜规则
              </h2>
              <ul className="mt-4 flex flex-col gap-3 text-sm leading-relaxed text-foreground-700">
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line mt-0.5 text-base text-accent-700"></i>
                  点赞量是基础门槛，但不会唯点赞论。
                </li>
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line mt-0.5 text-base text-accent-700"></i>
                  观点独到、有信息量、能代表多数人态度的优先。
                </li>
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line mt-0.5 text-base text-accent-700"></i>
                  含攻击、引战、不实信息的内容一律不上榜。
                </li>
              </ul>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">按分类找神评</h2>
              <ul className="mt-4 flex flex-col gap-1">
                {categories.map((c) => (
                  <li key={c.key}>
                    <Link
                      to={c.key === "all" ? "/events" : `/events?cat=${c.key}`}
                      className="flex items-center justify-between rounded-md px-3 py-2.5 text-sm text-foreground-700 transition-colors hover:bg-background-100"
                    >
                      <span className="flex items-center gap-2">
                        <i className={`${c.icon} text-base text-secondary-600`}></i>
                        {c.name}
                      </span>
                      <i className="ri-arrow-right-s-line text-base text-foreground-400"></i>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg bg-secondary-950 p-5 text-background-100">
              <h2 className="font-heading text-base font-bold text-background-50">你的神评也想上榜？</h2>
              <p className="mt-3 text-sm leading-relaxed text-background-100/75">
                加入吃瓜群，参与每天的新现场讨论，说出你的观点，下一条上榜的可能就是你。
              </p>
              <Link
                to="/about"
                className="mt-4 inline-flex items-center whitespace-nowrap rounded-md bg-accent-500 px-4 py-2.5 text-sm font-semibold text-foreground-950 transition-colors hover:bg-accent-400"
              >
                <i className="ri-group-line mr-1.5 text-base"></i>
                加入吃瓜群
              </Link>
            </div>
          </aside>
        </div>
      </section>
    </Layout>
  );
}